// import { convertToSnakeCase, generateRandomString } from "../utils/helpers";
// import { Asset, AssetListResponse, PaginationInfo } from "../types/asset.types";
import { convertToSnakeCase, generateRandomString } from "../common/helpers";
import { Asset, AssetListResponse, PaginationInfo } from "../models/asset.types";
import { callApi } from "./api";

const endpoints = {
  getAllAssets: "Geacloud_Assets/getAllAssets",
  getAssetById: "Geacloud_Assets/getAssetById",
  updateAsset: "Geacloud_Assets/updateAsset",
  searchAssets: "Geacloud_Assets/searchAssets",
  deleteAsset: "Geacloud_Assets/deleteAsset",
  createAsset: "Geacloud_Assets/createAsset",
};

// Normalize numeric fields from API response (convert strings to numbers)
const normalizeAsset = (asset: any): Asset => {
  return {
    ...asset,
    unit_value:
      typeof asset.unit_value === "string"
        ? parseFloat(asset.unit_value) || 0
        : asset.unit_value || 0,
    value:
      typeof asset.value === "string"
        ? parseFloat(asset.value) || 0
        : asset.value || 0,
    depreciation_value:
      typeof asset.depreciation_value === "string"
        ? parseFloat(asset.depreciation_value) || undefined
        : asset.depreciation_value,
    life_span:
      typeof asset.life_span === "string"
        ? parseInt(asset.life_span, 10) || undefined
        : asset.life_span,
    quantity:
      typeof asset.quantity === "string"
        ? parseInt(asset.quantity, 10) || 1
        : asset.quantity || 1,
  };
};

export const assetService = {
  getAll: async (
    page: number = 1,
    limit: number = 100,
    search?: string
  ): Promise<AssetListResponse> => {
    try {
      const params: any = {
        page,
        limit,
      };
      if (search) {
        params.search = search;
      }
      const response = await callApi(endpoints.getAllAssets, params);
      const data = response?.data?.data || {};
      const assets = (data.assets || []).map(normalizeAsset);
      const pagination: PaginationInfo = data.pagination || {
        page: 1,
        limit: 100,
        total: 0,
        total_pages: 0,
      };
      return { assets, pagination };
    } catch (error) {
      console.error(error);
      return {
        assets: [],
        pagination: {
          page: 1,
          limit: 100,
          total: 0,
          total_pages: 0,
        },
      };
    }
  },
  getById: async (asset_id: string): Promise<Asset | null> => {
    try {
      const response = await callApi(endpoints.getAssetById, { asset_id });
      const asset = response?.data?.data;
      return asset ? normalizeAsset(asset) : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  create: async (data: Partial<Asset>): Promise<Asset | null> => {
    const assetPayload = {
      ...data,
      id_tag: `${convertToSnakeCase(data.name)}-${generateRandomString()}`,
    };
    try {
      const response = await callApi(endpoints.createAsset, assetPayload);
      const asset = response?.data?.data;
      return asset ? normalizeAsset(asset) : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  update: async (
    id: string | number,
    data: Partial<Asset>
  ): Promise<Asset | null> => {
    try {
      const response = await callApi(endpoints.updateAsset, {
        asset_id: id,
        ...data,
      });
      const asset = response?.data?.data;
      return asset ? normalizeAsset(asset) : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  delete: async (id: string | number): Promise<boolean> => {
    try {
      const response = await callApi(endpoints.deleteAsset, { asset_id: id });
      return response?.data?.data?.success || true;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
  search: async (
    query: string,
    page: number = 1,
    limit: number = 100
  ): Promise<Asset[]> => {
    try {
      const params = {
        searchword: query,
        page,
        limit,
      };
      const response = await callApi(endpoints.searchAssets, params);
      const assets = response?.data?.data || {};
      return assets;
    } catch (error) {
      console.error(error);
      return [];
    }
  },
};
